import { nameof } from "ts-simple-nameof";
import * as yup from "yup";

import { LabelConstants, DomesticConstants } from "@assets/constants";
import { DomesticFormTypes } from "@assets/formTypes";
import { DomesticHelper, ValidationHelper } from "@assets/helpers";
import {
  CommonTypes,
  DomesticTypes,
  UserProfileViewModel,
} from "@assets/types";
import DomesticService from "@services/DomesticService";
import UserService from "@services/UserService";

import { AsyncValidationChecker } from "./common";

export class DomesticSSNValidationChecker extends AsyncValidationChecker {
  validateSSN = async (
    personSSN: string,
    triggerValidation: CommonTypes.CallbackFunction
  ) => {
    try {
      if (
        !personSSN ||
        personSSN.length < DomesticConstants.INPUT_CONSTRAINTS.SSN_LENGTH
      ) {
        this.isValid = false;
      } else {
        const isSSNValid = await DomesticService.validateSSN(personSSN);
        this.isValid = isSSNValid;
      }
      triggerValidation();
    } catch {
      // Ignore validation fail
    }
  };
}

export const cargoDetailValidationSchema = (isChristmasPackage: boolean) => {
  return yup.object({
    Description: yup
      .string()
      .trim()
      .max(DomesticConstants.INPUT_CONSTRAINTS.MAX_DETAIL_DESCRIPTION_LENGTH)
      .transform((value) => value.trim())
      .required(),
    Type: yup.mixed().required(),
    Quantity: yup
      .number()
      .min(1)
      .max(DomesticConstants.INPUT_CONSTRAINTS.MAX_QUANTITY)
      .required()
      .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    Weight: yup
      .number()
      .min(1)
      .max(DomesticConstants.INPUT_CONSTRAINTS.MAX_WEIGHT)
      .nullable()
      .transform(ValidationHelper.NaNToNull),
    WeightConfirmation: yup.boolean().when([], {
      is: () => isChristmasPackage,
      then: (schema) =>
        schema
          .required()
          .oneOf([true], LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
    }),
    HazardousMaterial: yup
      .mixed()
      .nullable()
      .when("Hazardous", {
        is: true,
        then: (schema) => schema.required(),
      }),
  });
};

export const domesticKioskSchema = yup.object({
  BookingNo: yup
    .string()
    .required()
    .matches(
      DomesticConstants.DOMESTIC_KIOSK_BOOKING_NO_REGEX,
      DomesticConstants.ERROR_LABELS.BOOKING_NOT_FOUND
    ),
});

export const domesticForm = (
  personSSNValidationChecker: DomesticSSNValidationChecker,
  userProfile: UserProfileViewModel | undefined,
  senderDepartments: DomesticTypes.DomesticDepartment[],
  receiverDepartments: DomesticTypes.DomesticDepartment[]
) => {
  const isUserAgentOrEmployee =
    UserService.isUserAgent(userProfile) ||
    UserService.isUserEmployee(userProfile);

  const personSSNSchema = yup
    .string()
    .max(DomesticConstants.INPUT_CONSTRAINTS.SSN_LENGTH);

  const nameSchema = yup.string().trim();

  const addressSchema = yup.string().trim();

  const postalCodeSchema = yup.mixed().nullable();

  const phoneSchema = yup
    .string()
    .trim()
    .matches(
      DomesticConstants.PHONE_REGEX,
      DomesticConstants.ERROR_LABELS.INVALID_PHONE
    );

  const emailSchema = yup
    .string()
    .max(DomesticConstants.INPUT_CONSTRAINTS.MAX_ADDRESS_LENGTH)
    .email()
    .trim();

  const checkIsSenderCustomerRequired = (
    iAm: DomesticConstants.BOOKING_PARTY,
    bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE
  ) =>
    isUserAgentOrEmployee ||
    (iAm === DomesticConstants.BOOKING_PARTY.SENDER &&
      bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.COMPANY &&
      !UserService.isUserCustomer(userProfile)) ||
    (iAm === DomesticConstants.BOOKING_PARTY.RECEIVER &&
      bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.COMPANY);

  const checkIsReceiverCustomerRequired = (
    iAm: DomesticConstants.BOOKING_PARTY,
    bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE
  ) =>
    isUserAgentOrEmployee ||
    (iAm === DomesticConstants.BOOKING_PARTY.RECEIVER &&
      bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.COMPANY &&
      !UserService.isUserCustomer(userProfile)) ||
    (iAm === DomesticConstants.BOOKING_PARTY.SENDER &&
      bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.COMPANY);

  const getBookingPartySchema = (
    bookingPartyTypeField: string,
    customerField: string,
    isNewAddressField: string,
    checkIsCustomerRequired: (
      bp: DomesticConstants.BOOKING_PARTY,
      bpt: DomesticConstants.BOOKING_PARTY_TYPE
    ) => boolean,
    departments: DomesticTypes.DomesticDepartment[]
  ) => {
    return {
      PartyType: yup.string(),
      Customer: yup
        .mixed()
        .nullable()
        .when(
          [
            nameof<DomesticFormTypes.CreateForm>((i) => i.BookingParty),
            bookingPartyTypeField,
          ],
          {
            is: (
              bookingParty: DomesticConstants.BOOKING_PARTY,
              bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE
            ) => checkIsCustomerRequired(bookingParty, bookingPartyType),
            then: (schema) => schema.required(),
          }
        ),
      SSN: yup.string().when(bookingPartyTypeField, {
        is: (bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE) =>
          bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.PERSON &&
          !isUserAgentOrEmployee,
        then: () =>
          personSSNSchema
            .required()
            .test("async-person-SSN-validation", "ERROR_SSN_INVALID", () =>
              personSSNValidationChecker.isInputValid()
            ),
      }),
      Department: yup
        .mixed()
        .nullable()
        .when([customerField, isNewAddressField], {
          is: (
            customer: DomesticTypes.DomesticCustomer | null,
            isNewAddress: boolean
          ) => customer !== null && departments.length > 0 && !isNewAddress,
          then: (schema) => schema.required(),
        }),
      Name: nameSchema.when(bookingPartyTypeField, {
        is: (bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE) =>
          bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.PERSON &&
          !isUserAgentOrEmployee,
        then: (schema) =>
          schema
            .max(DomesticConstants.INPUT_CONSTRAINTS.MAX_NAME_LENGTH)
            .required(),
      }),
      Address: addressSchema.when([bookingPartyTypeField, isNewAddressField], {
        is: (
          bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE,
          isNewAddress: boolean
        ) =>
          isNewAddress ||
          (bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.PERSON &&
            !isUserAgentOrEmployee),
        then: (schema) =>
          schema
            .max(DomesticConstants.INPUT_CONSTRAINTS.MAX_ADDRESS_LENGTH)
            .required(),
      }),
      PostalCode: postalCodeSchema.when(
        [bookingPartyTypeField, isNewAddressField],
        {
          is: (
            bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE,
            isNewAddress: boolean
          ) =>
            isNewAddress ||
            (bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.PERSON &&
              !isUserAgentOrEmployee),
          then: (schema) => schema.required(),
        }
      ),
      // Should be phoneSchema instead of yup.string(), but the auto-filled partners can't be trusted and validated
      Phone: yup
        .string()
        .when([bookingPartyTypeField, customerField, isNewAddressField], {
          is: (
            bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE,
            customer: DomesticTypes.DomesticCustomer,
            isNewAddress: boolean
          ) =>
            isNewAddress ||
            (bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.PERSON &&
              !isUserAgentOrEmployee) ||
            (customer &&
              customer.Source === DomesticConstants.CUSTOMER_SOURCE.NATREG),
          then: () => phoneSchema.required(),
        }),
      Email: yup
        .string()
        .when([bookingPartyTypeField, customerField, isNewAddressField], {
          is: (
            bookingPartyType: DomesticConstants.BOOKING_PARTY_TYPE,
            customer: DomesticTypes.DomesticCustomer,
            isNewAddress: boolean
          ) =>
            isNewAddress ||
            (bookingPartyType === DomesticConstants.BOOKING_PARTY_TYPE.PERSON &&
              !isUserAgentOrEmployee) ||
            (customer &&
              customer.Source === DomesticConstants.CUSTOMER_SOURCE.NATREG),
          then: () => emailSchema,
        }),
    };
  };

  const getPlaceSchema = (deliveryMode: string) => {
    return {
      Method: yup.string(),
      ContactName: nameSchema.when(deliveryMode, {
        is: (deliveryMode: DomesticConstants.DELIVERY_MODE) =>
          deliveryMode === DomesticConstants.DELIVERY_MODE.ADDRESS,
        then: (schema) =>
          schema
            .max(DomesticConstants.INPUT_CONSTRAINTS.MAX_CONTACT_NAME_LENGTH)
            .required(),
      }),
      ContactPhone: yup.string().when(deliveryMode, {
        is: (deliveryMode: DomesticConstants.DELIVERY_MODE) =>
          deliveryMode === DomesticConstants.DELIVERY_MODE.ADDRESS,
        then: () => phoneSchema.required(),
      }),
      PlaceDate: yup
        .date()
        .nullable()
        .when(deliveryMode, {
          is: (deliverMethod: DomesticConstants.DELIVERY_MODE) =>
            deliverMethod === DomesticConstants.DELIVERY_MODE.ADDRESS,
          then: (schema) =>
            schema
              .required()
              .typeError(LabelConstants.ERROR_LABELS.REQUIRED_FIELD),
        }),
    };
  };

  const senderSchema = getBookingPartySchema(
    nameof<DomesticFormTypes.CreateForm>((i) => i.SenderPartyType),
    nameof<DomesticFormTypes.CreateForm>((i) => i.SenderCustomer),
    nameof<DomesticFormTypes.CreateForm>((i) => i.SenderIsNewAddress),
    checkIsSenderCustomerRequired,
    senderDepartments
  );
  const receiverSchema = getBookingPartySchema(
    nameof<DomesticFormTypes.CreateForm>((i) => i.ReceiverPartyType),
    nameof<DomesticFormTypes.CreateForm>((i) => i.ReceiverCustomer),
    nameof<DomesticFormTypes.CreateForm>((i) => i.ReceiverIsNewAddress),
    checkIsReceiverCustomerRequired,
    receiverDepartments
  );
  const pickupSchema = getPlaceSchema(
    nameof<DomesticFormTypes.CreateForm>((i) => i.PickupMethod)
  );
  const dropOffSchema = getPlaceSchema(
    nameof<DomesticFormTypes.CreateForm>((i) => i.DropOffMethod)
  );

  return yup.object({
    // --- SENDER ---
    SenderPartyType: yup.string(),
    SenderCustomer: senderSchema.Customer,
    SenderSSN: senderSchema.SSN,
    SenderDepartment: senderSchema.Department,
    SenderName: senderSchema.Name,
    SenderAddress: senderSchema.Address,
    SenderPostalCode: senderSchema.PostalCode,
    SenderPhone: senderSchema.Phone,
    SenderEmail: senderSchema.Email,
    // --- RECEIVER ---
    ReceiverPartyType: yup.string(),
    ReceiverCustomer: receiverSchema.Customer,
    ReceiverSSN: receiverSchema.SSN,
    ReceiverDepartment: receiverSchema.Department,
    ReceiverName: receiverSchema.Name,
    ReceiverAddress: receiverSchema.Address,
    ReceiverPostalCode: receiverSchema.PostalCode,
    ReceiverPhone: receiverSchema.Phone,
    ReceiverEmail: receiverSchema.Email,
    // --- PICKUP METHODS ---
    PickupMethod: pickupSchema.Method,
    PickupContactName: pickupSchema.ContactName,
    PickupContactPhone: pickupSchema.ContactPhone,
    ReceiptDate: pickupSchema.PlaceDate,
    // --- DROP OFF METHODS ---
    DropOffMethod: dropOffSchema.Method,
    DropOffContactName: dropOffSchema.ContactName,
    DropOffContactPhone: dropOffSchema.ContactPhone,
    DeliveryDate: dropOffSchema.PlaceDate,
    // Payer is required when the separate payer legs isn't checked or when the booking is door to door
    Payer: yup
      .mixed()
      .nullable()
      .when(
        [
          nameof<DomesticFormTypes.CreateForm>((i) => i.PickupMethod),
          nameof<DomesticFormTypes.CreateForm>((i) => i.DropOffMethod),
          nameof<DomesticFormTypes.CreateForm>((i) => i.IsSeparatePayerLegs),
        ],
        {
          is: (
            pickupMethod: DomesticConstants.DELIVERY_MODE,
            dropOffMethod: DomesticConstants.DELIVERY_MODE,
            IsSeparatePayerLegs: boolean
          ) =>
            !IsSeparatePayerLegs ||
            (IsSeparatePayerLegs &&
              DomesticHelper.getPayerTermTypeofBooking(
                pickupMethod,
                dropOffMethod
              ) === DomesticConstants.PAYER_TERM_TYPE.D2D),
          then: (schema) =>
            schema
              .required(LabelConstants.ERROR_LABELS.OPTION_REQUIRED)
              .typeError(LabelConstants.ERROR_LABELS.OPTION_REQUIRED),
          otherwise: (schema) => schema,
        }
      ),
    CargoDetails: yup
      .array()
      .min(1, LabelConstants.ERROR_LABELS.REQUIRED_CARGO_LINE),
    Comment: yup.string(),
  });
};
